import { useCallback, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowRightIcon, CircleAlertIcon, TriangleAlertIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import * as yup from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { hasChanges } from '@/components/pages/Versions/TimelineBox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableRow,
  TableHeader,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { useAllRules } from '@/hooks/useAllRules';
import useBrains from '@/hooks/useBrains';
import useVersions from '@/hooks/useVersions';
import { popModal } from '@/redux/modals/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { Rule } from '@/shared/types/rules';

import { RuleLink } from './RuleLink';
import { VersionChanges } from './VersionChanges';

// Types
type FormValues = { version: string; description: string; selectedRules: string[] };

interface TableRowProps {
  versionToReplace: number;
  newVersion: number;
  rule: Rule & { desk_name: string };
  selected: boolean;
  onCheckedChange: (status: boolean) => void;
}

// Form validation
const formSchema = (maxVersionsReached: boolean) =>
  yup
    .object()
    .shape({
      version: maxVersionsReached ? yup.string().required() : yup.string(),
      description: yup.string(),
      selectedRules: yup.array().of(yup.string()),
    });

// Components
/**
 * Renders a table row for version changes
 * @param props - The table row properties
 * @returns JSX element representing a table row
 */
const VersionTableRow = ({
  versionToReplace,
  newVersion,
  rule,
  selected,
  onCheckedChange,
}: TableRowProps) => {
  return (
    <TableRow key={rule.rule_id}>
      <TableCell className="pl-5">
        <Checkbox checked={selected} onCheckedChange={onCheckedChange} />
      </TableCell>
      <TableCell className="pl-5">
        <RuleLink rule={rule} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="text-sm leading-5 font-normal text-foreground-muted">
            V{versionToReplace}
          </span>
          <ArrowRightIcon size={16} className="text-foreground" />
          <span className="text-sm leading-5 font-normal">V{newVersion}</span>
        </div>
      </TableCell>
    </TableRow>
  );
};

/**
 * Modal component for publishing new versions with rule selection
 * @param props - The modal properties
 * @returns JSX element representing the publish version modal
 */
const ModalPublishVersion = () => {
  // Hooks
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);
  const { rulesWithDeskName } = useAllRules({ brainId });
  const allRuleIds = useMemo(
    () => rulesWithDeskName?.map((rule) => rule.rule_id) || [],
    [rulesWithDeskName]
  );
  const {
    maxVersionsReached,
    versions,
    differences,
    differencesLoading,
    willReachVersionsLimit,
    publishVersionAsync,
    isPublishPending,
  } = useVersions({ brainId, enableDiff: true });
  const showAlert = willReachVersionsLimit || maxVersionsReached;
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form setup
  const form = useForm<FormValues>({
    values: { version: '', description: '', selectedRules: allRuleIds },
    resolver: yupResolver(formSchema(maxVersionsReached)) as Resolver<FormValues>,
  });

  // Derived state
  const nextVersion = brain?.versions;
  const selectedRules = useWatch({ control: form.control, name: 'selectedRules' });
  const isAllSelected = selectedRules.length === allRuleIds.length;
  const selectedVersion = useWatch({ control: form.control, name: 'version' });
  const activeSessionsCount = useMemo(
    () =>
      versions?.find((version) => {
        return version.version === Number(selectedVersion) && version.version !== 0;
      })?.active_sessions,
    [selectedVersion, versions]
  );
  // Event handlers
  const handleRuleToggle = (ruleId: string, checked: boolean) => {
    const currentRules = form.getValues('selectedRules');
    if (checked) {
      form.setValue('selectedRules', [...currentRules, ruleId]);
    } else {
      form.setValue(
        'selectedRules',
        currentRules.filter((id) => id !== ruleId)
      );
    }
  };

  const handleSelectAll = (checked: boolean) => {
    form.setValue('selectedRules', checked ? allRuleIds : []);
  };

  const handleOnClose = useCallback(() => {
    form.reset();
    dispatch(popModal());
  }, [form, dispatch]);

  const handleOnSubmit = useCallback(async () => {
    setIsSubmitting(true);
    const { description, selectedRules, version } = form.getValues();

    try {
      const replaceVersion = maxVersionsReached && selectedVersion ? parseInt(version) : undefined;
      const ruleIds = selectedRules.length > 0 ? selectedRules : undefined;

      const result = await publishVersionAsync({
        description,
        replace_version: replaceVersion ?? null,
        replace_rule_ids: ruleIds ?? null,
        force: replaceVersion ? true : undefined,
        hideToast: true,
      });

      const rulesChanged = result.rule_ids.length > 0;
      const toastTitle = rulesChanged
        ? t('versions.toast.version_published', { 0: result.version.version })
        : t('versions.toast.version_created', { 0: result.version.version });

      let descriptionText: string | undefined;

      if (replaceVersion) {
        descriptionText = t('versions.toast.version_replaced', { 0: version });
        if (activeSessionsCount > 0) {
          descriptionText += t('versions.toast.sessions_closed');
        }
      }
      toast.success(toastTitle, { description: descriptionText });
      handleOnClose();
    } catch {
      // Errors are already toasted in hooks; just ensure submitting state resets
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    publishVersionAsync,
    maxVersionsReached,
    selectedVersion,
    t,
    handleOnClose,
    activeSessionsCount,
  ]);

  // Memoized values
  const options = useMemo(
    () =>
      versions
        ?.map((version) => ({
          value: version.version.toString(),
          label: `Version ${version.version}`,
        }))
        .filter((version) => version.value !== '0'),
    [versions]
  );

  return (
    <Dialog open onOpenChange={handleOnClose}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-4 px-0 sm:max-w-xl">
        <DialogHeader className="px-6">
          <DialogTitle>{t('versions.dialog_title')}</DialogTitle>
          <DialogDescription>{t('versions.dialog_description')}</DialogDescription>
        </DialogHeader>

        <div className="z-60 space-y-4 overflow-y-auto px-6">
          {/* Version limit warnings */}
          {showAlert && (
            <Alert variant="warning" className="bg-bg-warning-soft">
              {maxVersionsReached && <TriangleAlertIcon />}
              {willReachVersionsLimit && <CircleAlertIcon />}
              <AlertTitle className="flex items-center gap-2">
                {maxVersionsReached && t('versions.max_versions_reached_title')}
                {willReachVersionsLimit && t('versions.about_to_reach_max_versions_title')}
              </AlertTitle>
              <AlertDescription>
                {maxVersionsReached && t('versions.max_versions_reached_subtitle')}
                {willReachVersionsLimit && t('versions.about_to_reach_max_versions_subtitle')}
              </AlertDescription>
            </Alert>
          )}

          {/* Form fields */}
          <Form {...form}>
            {maxVersionsReached && (
              <FormField
                control={form.control}
                name="version"
                render={({ field }) => {
                  const error = form.formState.errors.version;
                  return (
                    <FormItem>
                      <FormLabel>{t('common.replace')}</FormLabel>
                      <div className="flex items-center gap-2">
                        <PopoverCombobox
                          error={Boolean(error)}
                          triggerPlaceholder={t('versions.select_version')}
                          maxWidth="w-1/2"
                          options={options || []}
                          value={field.value}
                          onChange={field.onChange}
                        />
                        {activeSessionsCount > 0 && (
                          <InfoTooltip
                            content={t('versions.active_sessions_tooltip', {
                              0: selectedVersion,
                              1: activeSessionsCount,
                              count: activeSessionsCount,
                            })}
                            asChild={false}
                            size="auto"
                            className="max-w-xs"
                          >
                            <Badge
                              className="h-6"
                              type="outline"
                              rounded={false}
                              flavour="warning"
                              leadingIcon={<CircleAlertIcon />}
                            >
                              {t('versions.needs_attention')}
                            </Badge>
                          </InfoTooltip>
                        )}
                      </div>
                      {error && <FormMessage>{t('validation.required')}</FormMessage>}
                      <FormDescription>{t('versions.select_version_description')}</FormDescription>
                    </FormItem>
                  );
                }}
              />
            )}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.description')}</FormLabel>
                  <Input
                    {...field}
                    className="mb-1"
                    placeholder={t('common.description_placeholder')}
                  />
                </FormItem>
              )}
            />
          </Form>

          {/* Version changes */}
          {differences && hasChanges(differences) && (
            <>
              <Label>{t('common.changes')}</Label>
              <VersionChanges versionsDiff={differences} loadingChanges={differencesLoading} />
            </>
          )}

          {/* Rules selection table */}
          {rulesWithDeskName?.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">
                    <Checkbox checked={isAllSelected} onCheckedChange={handleSelectAll} />
                  </TableHead>
                  <TableHead className="pl-5">{t('versions.assign_to_rule')}</TableHead>
                  <TableHead>{t('versions.replace_version')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rulesWithDeskName?.map((rule) => (
                  <VersionTableRow
                    key={rule.rule_id}
                    versionToReplace={rule.brain_version}
                    newVersion={nextVersion}
                    rule={rule}
                    selected={selectedRules.includes(rule.rule_id)}
                    onCheckedChange={(checked) => handleRuleToggle(rule.rule_id, checked)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter className="px-6">
          <Button onClick={handleOnClose} variant="ghost-neutral" className="text-foreground-muted">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={form.handleSubmit(handleOnSubmit)}
            isLoading={isSubmitting || isPublishPending}
          >
            {t('versions.publish')} v{nextVersion}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalPublishVersion;
